import { approveWork, updateWork } from 'api/work'
import { useMutation } from '@tanstack/react-query'
import { shouldShowApproveButton } from 'lib/work'
import { useInvalidateShiftDetail } from 'queries/company/useCompanyShiftDetail'
import {
  useInvalidateReviewEvaluateShifts,
  useInvalidateReviewOvertimeApprovalShifts,
  useInvalidateWorkReview,
} from 'queries/review'
import { MutationOptions, QueryFnParams } from 'queries/types'
import { useUpdateTimelineShift } from '../shift'
import { useAuthContext } from 'components/auth'

/** Required override because mutationFn calls more than one function */
type SaveWorkRequest = QueryFnParams<typeof updateWork>[2]

/** Saves or approves edited work; chooses approve vs update from work status/times. */
export function useSaveWorkMutation(
  {
    editedWork,
    shiftId,
  }: {
    editedWork: Parameters<typeof shouldShowApproveButton>[0]
    shiftId: number
  },
  options: MutationOptions<(data: SaveWorkRequest) => Promise<void>> = {}
) {
  const { company } = useAuthContext()

  const { invalidate } = useInvalidateShiftDetail([shiftId])
  const { invalidate: invalidateReviewOvertimeApprovalShifts } =
    useInvalidateReviewOvertimeApprovalShifts()
  const { invalidate: invalidateReviewEvaluateShifts } =
    useInvalidateReviewEvaluateShifts()
  const { invalidate: invalidateWorkReview } = useInvalidateWorkReview()
  const { updateShift: updateTimelineShift } = useUpdateTimelineShift()

  return useMutation({
    ...options,
    mutationFn: async (data: SaveWorkRequest) => {
      if (!company?.id || shiftId === -1) {
        return
      }

      if (shouldShowApproveButton(editedWork)) {
        await approveWork(company.id, shiftId, data)
        return
      }

      await updateWork(company.id, shiftId, data)
    },
    onSuccess: (data, variables, context) => {
      updateTimelineShift(shiftId)
      invalidateReviewOvertimeApprovalShifts()
      invalidateReviewEvaluateShifts()
      invalidateWorkReview()
      invalidate()
      options.onSuccess?.(data, variables, context)
    },
  })
}
